// Meeting Redirect Page - Redirects to external calendar link via private code
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, Calendar, ArrowLeft, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MeetingRedirectPage() {
  const { slug, privateCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarUrl, setCalendarUrl] = useState(null);
  const [profileName, setProfileName] = useState('');

  useEffect(() => {
    const fetchMeetingConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/pages/${slug}/meeting/${privateCode}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError('Meeting link not found');
          } else {
            setError('Unable to load meeting link');
          }
          return;
        }
        const data = await res.json();
        setCalendarUrl(data.calendar_url);
        setProfileName(data.profile_name || '');
        
        // Auto-redirect after a brief moment
        if (data.calendar_url) {
          setTimeout(() => {
            window.location.href = data.calendar_url;
          }, 1500);
        }
      } catch (err) {
        console.error('Failed to fetch meeting config:', err);
        setError('Unable to load meeting link');
      } finally {
        setLoading(false);
      }
    };

    fetchMeetingConfig();
  }, [slug, privateCode]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#FAFAFA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={32} className="animate-spin" style={{ color: '#3B82F6', margin: '0 auto 16px' }} />
          <p style={{ color: '#64748B', fontSize: '14px' }}>Loading calendar...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#FAFAFA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}>
        <div style={{
          maxWidth: '400px',
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '32px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: '#FEF2F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Calendar size={28} style={{ color: '#EF4444' }} />
          </div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1E293B', marginBottom: '8px' }}>
            {error}
          </h2>
          <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '24px' }}>
            This meeting link may have been changed or is no longer available.
          </p>
          <Link
            to={`/p/${slug}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              color: '#3B82F6',
              textDecoration: 'none',
              fontWeight: '500',
            }}
          >
            <ArrowLeft size={16} />
            Back to profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#FAFAFA',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        maxWidth: '400px',
        background: '#FFFFFF',
        borderRadius: '16px',
        padding: '32px',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <Calendar size={28} style={{ color: '#FFFFFF' }} />
        </div>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1E293B', marginBottom: '8px' }}>
          Redirecting to calendar...
        </h2>
        <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '24px' }}>
          {profileName ? `Book a meeting with ${profileName}` : 'Opening booking page'}
        </p>
        
        <Loader2 size={20} className="animate-spin" style={{ color: '#3B82F6', margin: '0 auto 16px' }} />
        
        <a
          href={calendarUrl}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            color: '#3B82F6',
            textDecoration: 'none',
            fontWeight: '500',
          }}
        >
          <ExternalLink size={16} />
          Click here if not redirected
        </a>
      </div>
    </div>
  );
}
